import { API } from "aws-amplify";
import { action, flow, makeObservable, observable } from "mobx";
import { getAgent } from "../../../graphql/queries";
import { getConversationHistory, getConversationMessages } from "../../../services/conversations";
import dayjs from 'dayjs'

class HistoryAgentStore {
    agents = [];
    name = "";
    id = "";
    isLoadingAgents = false;
    isLoadingConcierge = false;
    isLoadingConversations = false;
    isLoadingMessages = false;
    isError = false;
    actions = [];
    conciergeAgent = null;
    isChatQuestionsVisible = true;
    bottomRef = null;
    lastConversationID = "";
    selectedConversationId = null;
    isLoadingConversation = false;
    shouldRefreshMessages = false;
    conversationStarters = [
        "What is your main purpose?",
        "How can you assist me?",
        "Tell me about your capabilities.",
        "What specific knowledge do you have available?",
    ]
    agentQuestions = [];
    conversations = [];
    messagesData = [];
    user_id = "";
    selectedAgent = null;

    constructor() {
        makeObservable(this, {
            agents: observable,
            name: observable,
            id: observable,
            isLoadingAgents: observable,
            isLoadingConcierge: observable,
            isLoadingConversations: observable,
            isLoadingMessages: observable,
            isError: observable,
            actions: observable,
            conciergeAgent: observable,
            isChatQuestionsVisible: observable,
            bottomRef: observable,
            agentQuestions: observable,
            conversations: observable,
            messagesData: observable,
            lastConversationID: observable,
            user_id: observable,
            selectedConversationId: observable,
            isLoadingConversation: observable,
            shouldRefreshMessages: observable,
            selectedAgent: observable,
            getAgents: flow,
            fetchConciergeAgent: flow,
            fetchConversations: flow,
            fetchMessages: flow,
            setIsChatQuestionsVisible: action,
            setBottomRef: action,
            setConversationsStarters: action,
            setLastConversationID: action,
            addConversation: action,
            clearMessagesData: action,
            setSelectedConversationId: action,
            setIsLoadingConversation: action,
            setShouldRefreshMessages: action,
            setSelectedAgent: action,
            fetchConversationsHTTP: flow,
            fetchMessagesHTTP: flow,
        });
    }

    *getAgents(customer_id, agent_id) {
        try {
            this.isLoadingAgents = true;
            this.isError = false;

            // Check if agents are already fetched
            // GraphQL removed - commented out
            // if (!this.agents.length) {
            //     const response = yield API.graphql({
            //         query: getAgent,
            //         variables: { input: { customer_id } },
            //         authMode: 'AMAZON_COGNITO_USER_POOLS'
            //     });
            //     const agents = JSON.parse(response.data?.getAgent?.body);
            //     this.agents = agents.filter((agent) => agent.provider === "openai");
            // }

            const selectedAgent = this.agents.find(agent => agent.id === agent_id || agent.uuid === agent_id);

            if (selectedAgent) {
                this.name = selectedAgent.name;
                this.id = selectedAgent.id || selectedAgent.uuid;
                this.actions = selectedAgent.actions;
            }

            return this.agents;
            
        } catch (error) {
            this.isError = true;
        } finally {
            this.isLoadingAgents = false;
        }
    }

    *fetchConciergeAgent(customer_id) {
        try {
            this.isLoadingConcierge = true;
            this.isError = false;

            // Check if conciergeAgent is already fetched
            // GraphQL removed - commented out
            // if (!this.conciergeAgent) {
            //     const response = yield API.graphql({
            //         query: getAgent,
            //         variables: { input: { customer_id, domain: "concierge" } },
            //         authMode: 'AMAZON_COGNITO_USER_POOLS'
            //     });
            //     const agents = JSON.parse(response.data.getAgent.body).filter(
            //         (agent) => agent.provider === "openai"
            //     );
            //     this.conciergeAgent = agents[0];
            // }
        } catch (error) {
            this.isError = true;
        } finally {
            this.isLoadingConcierge = false;
        }
    }

    setIsChatQuestionsVisible(visible) {
        this.isChatQuestionsVisible = visible;
    }

    setBottomRef(ref) {
        this.bottomRef = ref;
    }

    setConversationsStarters = (selectedAgent) => {
        if (
            selectedAgent?.conversation_starters.length &&
            !selectedAgent?.conversation_starters.every((el) => !el)
        ) {
            this.agentQuestions = selectedAgent.conversation_starters;
        } else {
            this.agentQuestions = this.conversationStarters;
        }
    };

    setSelectedAgent = (agent) => {
        this.selectedAgent = agent;
    };

    // GraphQL fetchConversations removed - use fetchConversationsHTTP instead
    *fetchConversations(agentId, userId) {
        console.warn('⚠️ fetchConversations (GraphQL) is deprecated. Use fetchConversationsHTTP instead.');
        // Redirect to HTTP method
        yield this.fetchConversationsHTTP(agentId, userId);
    }

    // GraphQL fetchMessages removed - use fetchMessagesHTTP instead
    *fetchMessages(conversationID) {
        console.warn('⚠️ fetchMessages (GraphQL) is deprecated. Use fetchMessagesHTTP instead.');
        // Redirect to HTTP method
        yield this.fetchMessagesHTTP(conversationID);
    }

    setLastConversationID(conversationId) {
        this.lastConversationID = conversationId
    }

    addConversation = (conversation) => {
        const existingConversationIndex = this.conversations.findIndex(
            (conv) => conv.id === conversation.id
        );

        if (existingConversationIndex !== -1) {
            // Update the existing conversation
            this.conversations[existingConversationIndex] = {
                ...this.conversations[existingConversationIndex],
                ...conversation,
            };
        } else {
            // Add a new conversation
            this.conversations.unshift(conversation);
        }
    };

    clearMessagesData = (() => {
        this.messagesData = [];
    });

    setSelectedConversationId = (conversationId) => {
        this.selectedConversationId = conversationId;
    };

    setIsLoadingConversation(isLoading) {
        this.isLoadingConversation = isLoading;
    }

    setShouldRefreshMessages(shouldRefresh) {
        this.shouldRefreshMessages = shouldRefresh;
    }

    // HTTP-based methods for new agent endpoints
    *fetchConversationsHTTP(agentId, userId) {
        try {
            this.isLoadingConversations = true;
            
            if (!this.isLoadingConversation) {
                this.clearMessagesData();
            }
            
            const response = yield getConversationHistory({ userId, agentId });
            this.user_id = userId;
            
            // Map new API response structure to match expected format
            // New API returns: { body: [...], meta: {...} }
            // Each conversation has: conversationUuid, title, lastUpdate, startedAt, participants
            const conversationsList = response.body || response.conversations || [];
            
            const conversations = conversationsList
                .sort((a, b) => {
                    const dateA = new Date(a.lastUpdate || a.startedAt || 0);
                    const dateB = new Date(b.lastUpdate || b.startedAt || 0);
                    return dateB - dateA;
                })
                .map(conv => ({
                    id: conv.conversationUuid, // Use conversationUuid as id
                    uuid: conv.conversationUuid,
                    conversationUuid: conv.conversationUuid,
                    title: conv.title,
                    last_modified: conv.lastUpdate || conv.startedAt,
                    startedAt: conv.startedAt,
                    agent_id: agentId, // Use the agentId passed to the function
                    participants: conv.participants || []
            }));

            // Ensure no duplicates
            this.conversations = conversations.filter((conv, index, self) =>
                    index === self.findIndex((t) => (
                        t.id === conv.id || t.uuid === conv.uuid || t.conversationUuid === conv.conversationUuid
                    ))
            );
        } catch (error) {
            console.error('Error fetching conversations (HTTP):', error);
            this.isError = true;
            this.conversations = [];
        } finally {
            this.isLoadingConversations = false;
        }
    }

    *fetchMessagesHTTP(conversationID) {
      try {
        this.isLoadingMessages = true;
        const response = yield getConversationMessages({
          conversationId: conversationID,
        });
  
        let raw = [];
        if (Array.isArray(response?.body)) {
          raw = response.body;
        }
  
        console.log("Response (HTTP):", response);
  
        const normalized = raw.map((m) => {
          const created = m.createdAt;
          const text = m.formattedText ?? m.messageText ?? m.message ?? m.content ?? m.text ?? m.body ?? "";
          const senderId = m.senderUuid;
          const senderRole = m.senderRole; // "USER" | "AGENT"
          const direction = m.direction; // "INBOUND" | "OUTBOUND"
  
          return { created, text, senderId, senderRole, direction };
        });
  
        const sorted = normalized.sort((a, b) => {
          const da = a.created ? new Date(a.created).getTime() : 0;
          const db = b.created ? new Date(b.created).getTime() : 0;
          return da - db;
        });
  
        this.messagesData = sorted.map((m) => {
          // Prefer role/direction when available
          const roleIsUser = typeof m.senderRole === 'string' && m.senderRole.toUpperCase() === 'USER';
          const dirIsInbound = typeof m.direction === 'string' && m.direction.toUpperCase() === 'INBOUND';
          const isSentByUser =
            roleIsUser || dirIsInbound || (m.senderId != null && String(m.senderId) === String(this.user_id));
          const timeStr = m.created ? dayjs(m.created).format("HH:mm") : "";
          return {
            time: timeStr,
            message: m.text,
            type: isSentByUser ? "sent" : "received",
          };
        });
        console.log("Processed messages (HTTP):", this.messagesData);
      } catch (error) {
        console.error("Error fetching messages (HTTP):", error);
        this.isError = true;
      } finally {
        this.isLoadingMessages = false;
      }
    }
}

export default HistoryAgentStore;